import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Film, CheckCircle2, AlertTriangle, X, Pencil, Ban, Clock } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { toast } from "sonner";

const ACTIVE = new Set(["generating", "rendering", "queued"]);
const CANCELABLE = new Set(["generating", "rendering", "queued"]);
const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

function StatusLine({ status }) {
  if (status === "error")
    return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-400"><AlertTriangle size={12} /> Failed</span>;
  if (status === "cancelled")
    return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-white/45"><Ban size={12} /> Cancelled</span>;
  if (status === "cancelling")
    return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-orange-300"><Loader2 size={12} className="spin" /> Cancelling…</span>;
  if (status === "ready")
    return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#a9d79b]"><CheckCircle2 size={12} /> Ready</span>;
  if (status === "queued")
    return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-white/60"><Loader2 size={12} className="spin" /> Queued…</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#ffd21e]"><Loader2 size={12} className="spin" /> Rendering…</span>;
}

const editorPath = (it) => (it.mode === "minute" ? `/minute/${it.id}` : `/manual/${it.id}/complete`);

export default function RenderTracker({ onItemReady }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);      // live items from server (+ synthesized ready ones)
  const [open, setOpen] = useState(false);
  const prevActive = useRef(new Map());          // id -> last-seen item while it was active
  const readyRef = useRef(new Map());            // id -> synthesized "ready" item
  const [avgSeconds, setAvgSeconds] = useState(0);
  const [, setTick] = useState(0);               // 1s heartbeat to advance the stopwatch
  const firstSeenRef = useRef(null);             // timestamp when the current active batch began

  useEffect(() => { api.get("/minute/render-eta").then(({ data }) => setAvgSeconds(data?.avg_seconds || 0)).catch(() => {}); }, []);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/me/render-queue");
      const live = Array.isArray(data) ? data : [];
      const liveIds = new Set(live.map((i) => i.id));

      // Anything that WAS active last tick but has now vanished entirely finished cleanly.
      prevActive.current.forEach((it, id) => {
        if (!liveIds.has(id)) {
          readyRef.current.set(id, { ...it, status: "ready", progress: 100 });
          if (onItemReady) onItemReady();
        }
      });

      const nextActive = new Map();
      live.forEach((i) => { if (ACTIVE.has(i.status)) nextActive.set(i.id, i); });
      prevActive.current = nextActive;
      // Stopwatch anchor: starts when the first active render appears, resets when none remain.
      if (nextActive.size > 0) { if (!firstSeenRef.current) firstSeenRef.current = Date.now(); }
      else { firstSeenRef.current = null; }

      liveIds.forEach((id) => readyRef.current.delete(id));

      const merged = [...live, ...Array.from(readyRef.current.values())]
        .filter((i, idx, a) => a.findIndex((x) => x.id === i.id) === idx);
      setItems(merged);
    } catch { /* keep the last snapshot */ }
  }, [onItemReady]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 3000);
    return () => clearInterval(t);
  }, [poll]);

  // 1s heartbeat so the pill stopwatch ticks smoothly between polls.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const activeCount = items.filter((i) => ACTIVE.has(i.status)).length;
  if (activeCount === 0 && !open) return null;
  const elapsed = firstSeenRef.current ? Math.floor((Date.now() - firstSeenRef.current) / 1000) : 0;

  const openView = (it) => { setOpen(false); navigate(editorPath(it)); };
  const goToEditor = () => {
    const target = items.find((i) => ACTIVE.has(i.status)) || items[0];
    if (target) { setOpen(false); navigate(editorPath(target)); }
  };
  const dismiss = (id) => {
    readyRef.current.delete(id);
    setItems((xs) => xs.filter((x) => x.id !== id));
  };
  const cancelRender = async (it) => {
    setItems((xs) => xs.map((x) => (x.id === it.id ? { ...x, status: "cancelling" } : x)));
    prevActive.current.delete(it.id);
    try {
      await api.post(`/manuals/${it.id}/render/cancel`);
      toast.message("Cancelling render — your credit will be refunded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel this render.");
      poll();
    }
  };

  return (
    <>
      {/* Header pill — filmstrip + live count */}
      {activeCount > 0 && (
        <button
          type="button"
          data-testid="render-tracker-pill"
          onClick={() => setOpen(true)}
          title="Videos rendering — tap to track progress"
          className="inline-flex items-center gap-2.5 rounded-2xl bg-[#14141b] border-2 border-[#5d8a52] pl-2.5 pr-3.5 py-1.5 hover:border-[#8fbf82] transition-colors"
        >
          <span className="relative flex items-center justify-center">
            <Film size={22} className="text-[#ffd21e]" />
            <Loader2 size={11} className="spin text-[#a9d79b] absolute -right-1 -bottom-1" />
          </span>
          <span className="tabular-nums bg-[#5d8a52] text-white rounded-full min-w-[20px] h-[20px] px-1 inline-flex items-center justify-center text-[11px] font-extrabold">{activeCount}</span>
          <span className="flex flex-col items-start leading-tight text-left">
            <span className="inline-flex items-center gap-1.5 text-xs font-extrabold text-white tabular-nums" data-testid="render-pill-stopwatch">
              Rendering <Clock size={12} className="text-[#ffd21e]" /> {fmtTime(elapsed)}
            </span>
            <span className="text-[10px] font-bold text-white/45 tabular-nums" data-testid="render-pill-avg">
              {avgSeconds > 0 ? `typical ~${fmtTime(avgSeconds)}` : "tap to track progress"}
            </span>
          </span>
        </button>
      )}

      {/* Print-queue lightbox */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
          data-testid="render-tracker-modal"
          onClick={() => setOpen(false)}
        >
          <div
            className="sms-tweed w-full max-w-md rounded-2xl bg-[#171426] border-2 border-[#5d8a52] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Film size={18} className="text-[#ffd21e]" />
                <h2 className="text-base font-extrabold text-white">Render Queue</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="render-goto-editor"
                  onClick={goToEditor}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#1e3aaa] hover:bg-[#2544c9] text-white text-xs font-bold px-3 py-1.5 transition-colors"
                >
                  <Pencil size={13} /> Go To Editor
                </button>
                <button data-testid="render-tracker-close" onClick={() => setOpen(false)} className="text-white/50 hover:text-white">
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="max-h-[60vh] overflow-y-auto divide-y divide-white/5">
              {items.length === 0 && (
                <p className="px-5 py-8 text-center text-sm text-white/50">Nothing in the queue right now.</p>
              )}
              {items.map((it) => {
                const done = it.status === "ready";
                const failed = it.status === "error";
                const cancelled = it.status === "cancelled";
                const canCancel = it.mode === "minute" && CANCELABLE.has(it.status);
                const pct = failed ? 100 : cancelled ? 0 : Math.max(4, Math.min(100, it.progress || 0));
                const barColor = failed ? "#dc2626" : done ? "#5d8a52" : cancelled ? "#4b4b57" : "#ffd21e";
                return (
                  <div key={it.id} data-testid={`render-item-${it.id}`} className="flex items-center gap-3 px-5 py-3.5">
                    <div className="shrink-0 w-11 h-14 rounded-lg overflow-hidden bg-[#0f0f16] border border-white/10 flex items-center justify-center">
                      {it.thumbnail_id
                        ? <img src={imageUrl(it.thumbnail_id)} alt="" className="w-full h-full object-cover" />
                        : <Film size={16} className="text-white/30" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-white">{it.title}</p>
                      <div className="mt-1.5 flex items-center justify-between gap-2">
                        <StatusLine status={it.status} />
                        {!failed && !cancelled && <span className="text-[11px] font-extrabold text-white/70">{pct}%</span>}
                      </div>
                      <div className="mt-1.5 h-1.5 w-full rounded-full bg-[#0f0f16] overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${pct}%`, background: barColor }} />
                      </div>
                      {failed && it.error && <p className="mt-1.5 text-[11px] text-red-400/90">{it.error}</p>}
                    </div>
                    {canCancel && (
                      <button
                        data-testid={`render-cancel-${it.id}`}
                        onClick={() => cancelRender(it)}
                        title="Cancel render (refunds your credit)"
                        className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#dc2626]/15 hover:bg-[#dc2626] text-red-400 hover:text-white transition-colors"
                      >
                        <X size={17} strokeWidth={3} />
                      </button>
                    )}
                    {done && (
                      <button
                        data-testid={`render-open-${it.id}`}
                        onClick={() => openView(it)}
                        className="shrink-0 rounded-lg bg-[#5d8a52] hover:bg-[#6c9d5f] text-white text-xs font-bold px-3 py-1.5 transition-colors"
                      >
                        Open
                      </button>
                    )}
                    {(done || failed || cancelled) && (
                      <button
                        data-testid={`render-dismiss-${it.id}`}
                        onClick={() => dismiss(it.id)}
                        className="shrink-0 text-white/30 hover:text-white/70"
                        title="Dismiss"
                      >
                        <X size={15} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="px-5 py-3.5 border-t border-white/10 bg-[#0f0f16]">
              <p className="text-[11px] leading-relaxed text-white/55 text-center">
                It's OK to close this window — your videos keep rendering on the server. Track their progress here on your Dashboard any time.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
